/**
 * useManualBroadcasts — хук для работы с ручными рассылками
 * Дата: 26.02.2026 — PLAN Manual Broadcasts
 */
import { useState, useEffect, useCallback } from 'react';
import { manualBroadcastsApi, type ManualBroadcast, type CreateBroadcastRequest } from '@/api/manualBroadcasts';

interface UseManualBroadcastsOptions {
  tenantId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export function useManualBroadcasts(options: UseManualBroadcastsOptions = {}) {
  const { tenantId, status, limit = 20, offset = 0 } = options;
  const [broadcasts, setBroadcasts] = useState<ManualBroadcast[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) {
      setBroadcasts([]);
      setTotal(0);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { limit: String(limit), offset: String(offset) };
      if (status) params.status = status;
      if (tenantId) params.tenant_id = tenantId;
      const res = await manualBroadcastsApi.list(params);
      setBroadcasts(res.broadcasts ?? []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      setError(e?.message ?? 'Ошибка загрузки рассылок');
      setBroadcasts([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, status, limit, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(async (data: CreateBroadcastRequest) => {
    const b = await manualBroadcastsApi.create(data, tenantId);
    await load();
    return b;
  }, [load, tenantId]);

  const launch = useCallback(async (id: string) => {
    await manualBroadcastsApi.launch(id, tenantId);
    await load();
  }, [load, tenantId]);

  const cancel = useCallback(async (id: string) => {
    await manualBroadcastsApi.cancel(id, tenantId);
    await load();
  }, [load, tenantId]);

  const remove = useCallback(async (id: string) => {
    await manualBroadcastsApi.delete(id, tenantId);
    await load();
  }, [load, tenantId]);

  return { broadcasts, total, loading, error, load, create, launch, cancel, remove };
}
